import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Form,
  Input,
  Select,
  Table,
  Modal,
  Tabs,
  Tag,
  Spin,
  Alert,
  Typography,
  Divider,
  Space,
  InputNumber,
  message
} from 'antd';
import {
  PlusOutlined,
  CreditCardOutlined,
  MobileOutlined,
  QrcodeOutlined,
  BankOutlined,
  ShopOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import api from '../utils/api';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const Payments = () => {
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('methods');

  // Payment amount for getting payment methods
  const [paymentAmount, setPaymentAmount] = useState(10000);

  useEffect(() => {
    loadPaymentMethods();
    loadPaymentHistory();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/payment-methods/gateway');
      setPaymentMethods(response.data.data || []);
    } catch (error) {
      console.error('Error loading payment methods:', error);
      message.error('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentHistory = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/payments/history');
      setPaymentHistory(response.data.transactions || []);
    } catch (error) {
      console.error('Error loading payment history:', error);
      message.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentIcon = (type) => {
    switch (type) {
      case 'virtual_account':
        return <BankOutlined style={{ fontSize: '24px', color: '#1890ff' }} />;
      case 'ewallet':
        return <MobileOutlined style={{ fontSize: '24px', color: '#52c41a' }} />;
      case 'qris':
        return <QrcodeOutlined style={{ fontSize: '24px', color: '#722ed1' }} />;
      case 'credit_card':
        return <CreditCardOutlined style={{ fontSize: '24px', color: '#fa541c' }} />;
      default:
        return <ShopOutlined style={{ fontSize: '24px', color: '#13c2c2' }} />;
    }
  };

  const getPaymentTypeColor = (type) => {
    switch (type) {
      case 'virtual_account':
        return 'blue';
      case 'ewallet':
        return 'green';
      case 'qris':
        return 'purple';
      case 'credit_card':
        return 'orange';
      default:
        return 'cyan';
    }
  };

  const paymentMethodsColumns = [
    {
      title: 'Payment Method',
      key: 'payment_method',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {getPaymentIcon(record.payment_type)}
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.payment_method_name}</div>
            <Text type="secondary">{record.provider}</Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'payment_type',
      key: 'payment_type',
      render: (type) => (
        <Tag color={getPaymentTypeColor(type)}>
          {type.replace('_', ' ').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category_name',
      render: (text, record) => (
        <div>
          <Tag color="blue">{record.category_code}</Tag>
          <div>{text}</div>
        </div>
      ),
    },
    {
      title: 'Subcategory',
      dataIndex: 'subcategory_name',
      key: 'subcategory_name',
      render: (text, record) => (
        <div>
          <Tag color="green">{record.subcategory_code}</Tag>
          <div>{text}</div>
        </div>
      ),
    },
    {
      title: 'Fee Structure',
      key: 'fee_structure',
      render: (_, record) => (
        <div>
          <div>
            <Text>Merchant: </Text>
            <Tag color="cyan">
              {record.fee_fixed > 0 && `Rp ${record.fee_fixed}`}
              {record.fee_percentage > 0 && ` +${(record.fee_percentage * 100).toFixed(2)}%`}
            </Tag>
          </div>
          <div>
            <Text>Type: </Text>
            <Tag color="purple">{record.fee_type}</Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Processing',
      key: 'processing',
      render: (_, record) => (
        <div>
          <div>{record.processing_time}</div>
          <Text type="secondary">Min: Rp {parseFloat(record.min_amount).toLocaleString()}</Text>
        </div>
      ),
    },
    {
      title: 'Supplier',
      key: 'supplier',
      render: (_, record) => (
        <div>
          <Tag color="orange">{record.supplier_code}</Tag>
          <div>{record.supplier_name}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
  ];

  const handleCreatePayment = async (values) => {
    try {
      setCreateLoading(true);

      const paymentData = {
        amount: values.amount,
        productDetails: values.productDetails,
        paymentMethod: values.paymentMethod,
        customerInfo: {
          name: values.customerName,
          email: values.customerEmail,
          phone: values.customerPhone,
          firstName: values.customerName.split(' ')[0],
          lastName: values.customerName.split(' ').slice(1).join(' ') || '',
          phoneNumber: values.customerPhone
        },
        expiryPeriod: values.expiryPeriod || 60
      };

      const response = await api.post('/api/payments/create', paymentData);

      if (response.data.success) {
        message.success('Payment created successfully');
        setCreateModalVisible(false);
        form.resetFields();
        loadPaymentHistory();

        // Show payment details modal
        Modal.info({
          title: 'Payment Created',
          width: 600,
          content: (
            <div>
              <p><strong>Transaction ID:</strong> {response.data.transaction.merchantOrderId}</p>
              <p><strong>Amount:</strong> Rp {response.data.transaction.amount.toLocaleString()}</p>
              <p><strong>Status:</strong> {response.data.transaction.status}</p>
              {response.data.transaction.paymentUrl && (
                <div>
                  <p><strong>Payment URL:</strong></p>
                  <a href={response.data.transaction.paymentUrl} target="_blank" rel="noopener noreferrer">
                    {response.data.transaction.paymentUrl}
                  </a>
                </div>
              )}
              {response.data.transaction.vaNumber && (
                <p><strong>VA Number:</strong> {response.data.transaction.vaNumber}</p>
              )}
              {response.data.transaction.qrString && (
                <p><strong>QR Code:</strong> {response.data.transaction.qrString}</p>
              )}
            </div>
          )
        });
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      message.error('Failed to create payment');
    } finally {
      setCreateLoading(false);
    }
  };

  const paymentHistoryColumns = [
    {
      title: 'Transaction ID',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `Rp ${amount?.toLocaleString()}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = status === 'success' ? 'green' : status === 'pending' ? 'orange' : 'red';
        return <Tag color={color}>{status?.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Customer',
      dataIndex: 'customer_email',
      key: 'customer_email',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Arendo</Title>
        <Text type="secondary">Kelola pembayaran melalui Duitku Arendo</Text>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Payment Methods" key="methods">
          <Card>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong>Available Payment Methods</Text>
                <br />
                <Text type="secondary">Metode pembayaran yang tersedia melalui Duitku</Text>
              </div>
              <Space>
                <Text>Amount:</Text>
                <InputNumber
                  value={paymentAmount}
                  onChange={setPaymentAmount}
                  min={1000}
                  step={1000}
                  formatter={value => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/Rp\s?|(,*)/g, '')}
                  style={{ width: 150 }}
                />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadPaymentMethods}
                  loading={loading}
                >
                  Refresh
                </Button>
              </Space>
            </div>

            <Divider />

            {loading ? (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={paymentMethodsColumns}
                dataSource={paymentMethods}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} payment methods`,
                }}
                scroll={{ x: 1200 }}
              />
            )}
          </Card>
        </TabPane>

        <TabPane tab="Create Payment" key="create">
          <Card title="Create New Payment">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleCreatePayment}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Amount"
                    name="amount"
                    rules={[{ required: true, message: 'Please enter amount' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1000}
                      step={1000}
                      formatter={value => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/Rp\s?|(,*)/g, '')}
                      placeholder="Enter payment amount"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Payment Method"
                    name="paymentMethod"
                    rules={[{ required: true, message: 'Please select payment method' }]}
                  >
                    <Select placeholder="Select payment method">
                      {paymentMethods.map((method, index) => (
                        <Option key={index} value={method.code}>
                          {method.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Product Details"
                name="productDetails"
                rules={[{ required: true, message: 'Please enter product details' }]}
              >
                <Input placeholder="Enter product description" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Customer Name"
                    name="customerName"
                    rules={[{ required: true, message: 'Please enter customer name' }]}
                  >
                    <Input placeholder="Enter customer name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Customer Email"
                    name="customerEmail"
                    rules={[
                      { required: true, message: 'Please enter customer email' },
                      { type: 'email', message: 'Please enter valid email' }
                    ]}
                  >
                    <Input placeholder="Enter customer email" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Customer Phone"
                    name="customerPhone"
                    rules={[{ required: true, message: 'Please enter customer phone' }]}
                  >
                    <Input placeholder="Enter customer phone number" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Expiry Period (minutes)"
                    name="expiryPeriod"
                    initialValue={60}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={15}
                      max={1440}
                      placeholder="Payment expiry in minutes"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={createLoading}
                  icon={<PlusOutlined />}
                  size="large"
                >
                  Create Payment
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane tab="Payment History" key="history">
          <Card
            title="Payment History"
            extra={
              <Button
                icon={<ReloadOutlined />}
                onClick={loadPaymentHistory}
                loading={loading}
              >
                Refresh
              </Button>
            }
          >
            <Table
              columns={paymentHistoryColumns}
              dataSource={paymentHistory}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }}
            />
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Payments;